import * as Yup from 'yup';

// eslint-disable-next-line import/prefer-default-export
export const getPrefilterValidationSchema = (translator) =>
  Yup.object().shape({
    primary_conditions: Yup.array().min(
      1,
      translator.prefilter_primary_condition,
    ),
    secondary_conditions: Yup.array().of(Yup.string()),
    treatment_type_id: Yup.object().shape({
      values: Yup.array().of(Yup.string()),
      must_have: Yup.boolean(),
      selected: Yup.boolean(),
    }),
    sex_id: Yup.object().shape({
      value: Yup.string(),
      must_have: Yup.boolean(),
      selected: Yup.boolean(),
    }),
    language_id: Yup.object().shape({
      values: Yup.array().of(Yup.string()),
      must_have: Yup.boolean(),
      selected: Yup.boolean(),
    }),
    degree_id: Yup.object().shape({
      value: Yup.string(),
      must_have: Yup.boolean(),
      selected: Yup.boolean(),
    }),
    ethnicity_ids: Yup.object().shape({
      values: Yup.array().of(Yup.string()),
      must_have: Yup.boolean(),
      selected: Yup.boolean(),
    }),
    race_id: Yup.object().shape({
      value: Yup.string(),
      must_have: Yup.boolean(),
      selected: Yup.boolean(),
    }),
  });

// Create mutually exclusive options for Primary and Secondary Conditions
export const getPrimaryConditionsOptions = (values, filterOptions) => {
  const selectedSecondaryConditions = values.secondary_conditions || [];
  return filterOptions.primary_conditions.filter(
    (option) => !selectedSecondaryConditions.includes(option.value),
  );
};

export const getSecondaryConditionsOptions = (values, filterOptions) => {
  const selectedPrimaryConditions = values.primary_conditions || [];
  return filterOptions.secondary_conditions.filter(
    (option) => !selectedPrimaryConditions.includes(option.value),
  );
};

export const prefFilterInitialValues = (data) => {
  const {
    primary_conditions,
    secondary_conditions,
    treatment_type_id,
    sex_id,
    language_id,
    degree_id,
    ethnicity_ids,
    race_id,
  } = data || {};
  return {
    primary_conditions: primary_conditions?.default || [],
    secondary_conditions: secondary_conditions?.default || [],
    treatment_type_id: {
      values: treatment_type_id?.default || [],
      must_have: treatment_type_id?.must_have || false,
      selected: !!treatment_type_id?.default?.length,
    },
    sex_id: {
      value: sex_id?.default || '',
      must_have: sex_id?.must_have || false,
      selected: !!sex_id?.default,
    },
    language_id: {
      values: language_id?.default || [],
      must_have: language_id?.must_have || false,
      selected: !!language_id?.default?.length,
    },
    degree_id: {
      value: degree_id?.default || '',
      must_have: degree_id?.must_have || false,
      selected: !!degree_id?.default,
    },
    ethnicity_ids: {
      values: ethnicity_ids?.default || [],
      must_have: ethnicity_ids?.must_have || false,
      selected: !!ethnicity_ids?.default?.length,
    },
    race_id: {
      value: race_id?.default || '',
      must_have: race_id?.must_have || false,
      selected: !!race_id?.default,
    },
  };
};

export const normalizeOptions = (options, keyMapping = ['value', 'label']) => {
  if (!options || !Array.isArray(options)) return [];

  return options.map((option) => {
    const [valueKey, labelKey] = keyMapping;

    // If the option already has value/label format, return as is
    if (option.value !== undefined && option.label !== undefined) {
      return option;
    }

    // Convert from custom format to {value, label}
    return {
      value: option[valueKey],
      label: option[labelKey],
    };
  });
};

export const buildFilterOptions = (data) => ({
  primary_conditions: normalizeOptions(data.primary_conditions?.options),
  secondary_conditions: normalizeOptions(data.secondary_conditions?.options),
  treatment_type_id: normalizeOptions(data.treatment_type_id?.options, [
    'id',
    'name',
  ]),
  sex_id: normalizeOptions(data.sex_id?.options),
  language_id: normalizeOptions(data.language_id?.options),
  degree_id: normalizeOptions(data.degree_id?.options),
  ethnicity_ids: normalizeOptions(data.ethnicity_ids?.options),
  race_id: normalizeOptions(data.race_id?.options),
});

export const buildFilters = (translator, filterOptions) => [
  {
    key: 'treatment_type_id',
    label: translator.types_of_therapy_provided,
    type: 'multi',
    options: filterOptions.treatment_type_id,
  },
  {
    key: 'sex_id',
    label: translator.gender,
    type: 'single',
    options: filterOptions.sex_id,
  },
  {
    key: 'language_id',
    label: translator.language,
    type: 'multi',
    options: filterOptions.language_id,
  },
  {
    key: 'degree_id',
    label: translator.education,
    type: 'single',
    options: filterOptions.degree_id,
  },
  {
    key: 'ethnicity_ids',
    label: translator.ethnicity,
    type: 'multi',
    options: filterOptions.ethnicity_ids,
  },
  {
    key: 'race_id',
    label: translator.race,
    type: 'single',
    options: filterOptions.race_id,
  },
];

export const openFilterDropdown = (filterKey) => {
  setTimeout(() => {
    const filterRow = document.querySelector(
      `[data-filter-key="${filterKey}"]`,
    );
    if (filterRow) {
      const control = filterRow.querySelector('.custom-react-select__control');
      if (control) {
        control.click();
        control.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      }
    }
  }, 100);
};

export const buildSubmitValues = (
  values = {},
  filterFieldsData = {},
  filters = {},
  selectedFilters = null,
) => {
  const primaryConditionName = filterFieldsData?.primary_conditions?.name;

  // 1. Initialize cleaned values with the pre_filter flag
  const cleanedValues = { ...values };
  const optionalKey = '_optional';

  // 2. Process dynamic filters
  filters.forEach((filter) => {
    const { key } = filter;

    // Remove filters not currently selected
    if (selectedFilters) {
      if (!selectedFilters.includes(key)) {
        delete cleanedValues[key];
      }
    } else if (!values[filter.key]?.selected) {
      delete cleanedValues[filter.key];
    } else {
      // Remove the selected property from the final output
      delete cleanedValues[filter.key].selected;
    }

    const data = cleanedValues[key];
    const isValueObject =
      data && typeof data === 'object' && !Array.isArray(data);

    if (isValueObject && ('values' in data || 'value' in data)) {
      const baseValue = data.values || data.value || [];
      const isMustHave = data.must_have === true;

      // Business Logic: Must-have values stay in the main key,
      // otherwise they move to the optional key.
      cleanedValues[key] = isMustHave ? baseValue : undefined;
      cleanedValues[`${key}${optionalKey}`] = isMustHave ? [] : baseValue;

      const optionalValue = cleanedValues[`${key}${optionalKey}`] || [];

      // Cleanup: Remove the optional key if it ends up empty/null
      if (optionalValue.length === 0)
        delete cleanedValues[`${key}${optionalKey}`];
      // Cleanup: Remove the main key if it ended up undefined
      if (cleanedValues[key] === undefined) delete cleanedValues[key];
    }
  });

  // 3. Handle primary/secondary conditions mapping
  if (primaryConditionName) {
    const primary = cleanedValues.primary_conditions || [];
    const secondary = cleanedValues.secondary_conditions || [];
    const optionalName = `${primaryConditionName}${optionalKey}`;

    // Map primary to the specific condition name
    if (primary.length > 0) {
      cleanedValues[primaryConditionName] = primary;
    } else {
      delete cleanedValues[primaryConditionName];
    }

    // Map secondary to the optional condition name
    if (secondary.length > 0) {
      cleanedValues[optionalName] = secondary;
    } else {
      delete cleanedValues[optionalName];
    }
  }
  return cleanedValues;
};
